import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle, FilterX, Headset, Inbox, MessageSquareWarning, Paperclip, Plus, RefreshCw, Timer,
} from 'lucide-react';
import {
  AppButton,
  AppDataTable,
  Callout,
  EmptyState,
  FilterToolbar,
  IconButton,
  PageHeader,
  ROLE_LABELS,
  SegmentedControl,
  StatsCard,
  StatsGrid,
  StatusBadge,
  UserAvatar,
  isColumnFilterActive,
} from '../ui';
import apiClient from '../../utils/apiClient';
import NewTicketModal from './NewTicketModal';
import TicketPriority from './TicketPriority';
import useTicketList from './useTicketList';
import {
  ACTIVE_STATUSES,
  CATEGORY_OPTIONS,
  PRIORITY_OPTIONS,
  STATUS_OPTIONS,
  categoryLabel,
  formatDate,
  formatDateTime,
  formatDuration,
  timeAgo,
} from './supportVocabulary';
import { getTicketCreatorName } from './supportIdentity';

const PAGE_SIZE = 20;

const AWAITING_OPTIONS = [
  { value: 'support', label: 'Needs reply' },
  { value: 'requester', label: 'Awaiting requester' },
];
const ASSIGNEE_OPTIONS = [
  { value: 'none', label: 'Unassigned' },
  { value: 'any', label: 'Assigned' },
];

/**
 * Quick views. Each is a preset of the SAME column filters the header funnels
 * write, so a view and a funnel can never disagree — pick "Urgent", open the
 * Priority funnel, and it shows High and Critical ticked.
 */
const VIEWS = [
  { value: 'active', label: 'Open', filters: { status: ACTIVE_STATUSES }, countOf: (s) => s.active },
  { value: 'needsReply', label: 'Needs reply', filters: { awaiting: ['support'] }, countOf: (s) => s.needsReply },
  { value: 'urgent', label: 'Urgent', filters: { status: ACTIVE_STATUSES, priority: ['high', 'critical'] }, countOf: (s) => s.urgent },
  { value: 'unassigned', label: 'Unassigned', filters: { status: ACTIVE_STATUSES, assignee: ['none'] }, countOf: (s) => s.unassigned },
  { value: 'all', label: 'All', filters: {}, countOf: (s) => s.total },
];

/** The view whose preset the filters match, ignoring the subject search. */
const sameSet = (a = [], b = []) => a.length === b.length && a.every((v) => b.includes(v));
const viewOf = (filters) => {
  const keys = Object.keys(filters).filter((k) => k !== 'title' && isColumnFilterActive(filters[k]));
  const match = VIEWS.find((v) => {
    const vk = Object.keys(v.filters);
    return vk.length === keys.length && vk.every((k) => sameSet(v.filters[k], filters[k]));
  });
  return match?.value || '';
};

const listParam = (value) => (Array.isArray(value) && value.length ? value.join(',') : undefined);

/** Column filters + sort + page → the endpoint's query. Every funnel maps to a real parameter. */
const toQuery = (filters, sort, page) => ({
  page,
  pageSize: PAGE_SIZE,
  search: filters.title?.trim() || undefined,
  status: listParam(filters.status),
  priority: listParam(filters.priority),
  category: listParam(filters.category),
  awaiting: listParam(filters.awaiting),
  assigned: listParam(filters.assignee),
  tenantId: listParam(filters.tenant),
  sortBy: sort.key,
  sortOrder: sort.direction,
});

/**
 * The support desk — the institute's queue (`/support/tickets`) or every
 * institute's (`/sa/support`, `scope="platform"`).
 *
 * The question it answers first is "what needs me": the tiles count what is
 * waiting, the views jump to it, and "Waiting on" says per ticket whether the
 * next move is ours or the requester's. Filtering and sorting are all
 * server-side, so a funnel narrows the whole queue, not the page on screen.
 *
 * Props:
 *   scope — 'institute' (default) | 'platform'
 */
const SupportDeskPage = ({ scope = 'institute' }) => {
  const navigate = useNavigate();
  const platform = scope === 'platform';
  const basePath = platform ? '/sa/support' : '/support/tickets';

  const [columnFilters, setFiltersState] = useState(VIEWS[0].filters);
  const [sort, setSortState] = useState({ key: 'updatedAt', direction: 'desc' });
  const [page, setPage] = useState(1);
  const [institutes, setInstitutes] = useState(null);
  const [composerOpen, setComposerOpen] = useState(false);

  // A new filter or sort starts from page 1 in the SAME render, so the list is
  // never fetched once more with the old page first.
  const setColumnFilters = (next) => { setFiltersState(next); setPage(1); };
  const setSort = (next) => { setSortState(next); setPage(1); };

  const query = toQuery(columnFilters, sort, page);
  const { tickets, total, totalPages, stats, loading, error, reload } = useTicketList(
    platform ? '/support/admin/tickets' : '/support/tickets',
    query,
  );

  useEffect(() => {
    if (!platform) return;
    apiClient.get('/sa/institutes')
      .then(({ data }) => setInstitutes((data.institutes || []).map((i) => ({ _id: i._id, name: i.name }))))
      .catch(() => setInstitutes([]));
  }, [platform]);

  const view = viewOf(columnFilters);
  const filtersOn = Object.values(columnFilters).some(isColumnFilterActive);
  // A pressed view already says what is filtered; the button is for the rest.
  const customFilters = filtersOn && (!view || isColumnFilterActive(columnFilters.title));

  const applyView = (value) => {
    const preset = VIEWS.find((v) => v.value === value)?.filters || {};
    setColumnFilters(columnFilters.title ? { ...preset, title: columnFilters.title } : preset);
  };

  const columns = useMemo(() => [
    {
      key: 'title',
      label: 'Ticket',
      minWidth: 260,
      width: 340,
      pinned: 'left',
      // The endpoint's search reads subject, description, ticket number and requester name.
      filter: { type: 'text', placeholder: 'Subject, number or requester' },
      render: (t) => (
        <div className="tw-min-w-0">
          <span className="tw-block tw-truncate tw-text-[0.8125rem] tw-font-semibold tw-text-content" title={t.title}>{t.title}</span>
          <span className="tw-mt-0.5 tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-content-subtle">
            <span className="tw-font-mono">{t.ticketId}</span>
            <span aria-hidden="true">·</span>
            <span title={formatDateTime(t.createdAt)}>Opened {formatDate(t.createdAt)}</span>
            {t.replyCount > 0 && (<><span aria-hidden="true">·</span><span>{t.replyCount} {t.replyCount === 1 ? 'reply' : 'replies'}</span></>)}
            {t.hasAttachment && <Paperclip size={12} role="img" aria-label="Has a screenshot" />}
          </span>
        </div>
      ),
    },
    {
      key: 'requester',
      label: 'Requester',
      width: 190,
      render: (t) => {
        const name = getTicketCreatorName(t);
        return (
          <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-2">
            <UserAvatar name={name} size="sm" />
            <span className="tw-min-w-0">
              <span className="tw-block tw-truncate tw-text-[0.8125rem] tw-text-content">{name}</span>
              <span className="tw-block tw-truncate tw-text-xs tw-text-content-subtle">
                {t.createdOnBehalf ? 'Filed on the institute’s behalf' : (ROLE_LABELS[t.role] || 'User')}
              </span>
            </span>
          </div>
        );
      },
    },
    ...(platform ? [{
      key: 'tenant',
      label: 'Institute',
      width: 170,
      filter: { type: 'set', options: (institutes || []).map((i) => ({ value: i._id, label: i.name })) },
      render: (t) => <span className="tw-block tw-truncate tw-text-[0.8125rem] tw-text-content">{t.tenantName || '—'}</span>,
    }] : []),
    {
      key: 'category',
      label: 'Category',
      width: 150,
      filter: { type: 'set', options: CATEGORY_OPTIONS },
      render: (t) => <span className="tw-text-[0.8125rem] tw-text-content-muted">{categoryLabel(t.category)}</span>,
    },
    {
      key: 'status',
      label: 'Status',
      width: 120,
      filter: { type: 'set', options: STATUS_OPTIONS },
      render: (t) => <StatusBadge status={t.status} domain="supportTicket" />,
    },
    {
      key: 'priority',
      label: 'Priority',
      width: 110,
      filter: { type: 'set', options: PRIORITY_OPTIONS },
      render: (t) => <TicketPriority priority={t.priority} />,
    },
    {
      key: 'awaiting',
      label: 'Waiting on',
      width: 150,
      filter: { type: 'set', options: AWAITING_OPTIONS },
      render: (t) => {
        if (t.awaiting === 'support') return <StatusBadge status="warning">Needs reply</StatusBadge>;
        if (t.awaiting === 'requester') return <span className="tw-text-[0.8125rem] tw-text-content-muted">Requester</span>;
        return <span className="tw-text-[0.8125rem] tw-text-content-subtle">—</span>;
      },
    },
    {
      key: 'assignee',
      label: 'Assignee',
      width: 140,
      filter: { type: 'set', options: ASSIGNEE_OPTIONS },
      render: (t) => (t.assignedTo
        ? <span className="tw-block tw-truncate tw-text-[0.8125rem] tw-text-content">{t.assignedTo}</span>
        : <span className="tw-text-[0.8125rem] tw-italic tw-text-content-subtle">Unassigned</span>),
    },
    {
      key: 'updatedAt',
      label: 'Updated',
      width: 120,
      sortable: true,
      render: (t) => (
        <span className="tw-whitespace-nowrap tw-text-[0.8125rem] tw-text-content-muted" title={formatDateTime(t.updatedAt)}>
          {timeAgo(t.updatedAt || t.createdAt)}
        </span>
      ),
    },
  ], [platform, institutes]);

  const s = stats || {};

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <PageHeader
        icon={Headset}
        title="Support desk"
        description={platform
          ? 'Requests from every institute, and the ones filed on their behalf.'
          : 'Every request from your students, trainers and staff.'}
      />

      <StatsGrid cols={4} collapsible storageKey={platform ? 'sa-support-desk' : 'support-desk'} title="Queue at a glance">
        <StatsCard
          title="Needs a reply"
          value={stats ? s.needsReply : '—'}
          subtitle="Open, and the last word was the requester’s"
          icon={MessageSquareWarning}
          iconColor="amber"
          loading={!stats && loading}
        />
        <StatsCard
          title="Open"
          value={stats ? s.active : '—'}
          subtitle={stats ? `${s.unassigned} unassigned · ${s.in_progress} in progress` : ' '}
          icon={Inbox}
          iconColor="blue"
          loading={!stats && loading}
        />
        <StatsCard
          title="Urgent"
          value={stats ? s.urgent : '—'}
          subtitle="High or critical, still open"
          icon={AlertTriangle}
          iconColor="rose"
          loading={!stats && loading}
        />
        <StatsCard
          title="First response"
          value={stats?.avgFirstResponseMs != null ? formatDuration(s.avgFirstResponseMs) : '—'}
          subtitle={stats?.respondedCount ? `Average over ${s.respondedCount} tickets, last 30 days` : 'No replies in the last 30 days'}
          icon={Timer}
          iconColor="emerald"
          loading={!stats && loading}
        />
      </StatsGrid>

      {error && !loading && (
        <Callout tone="danger" role="alert" action={<AppButton size="xs" variant="outline" onClick={reload}>Try again</AppButton>}>
          The queue couldn’t be loaded. Check your connection and try again.
        </Callout>
      )}

      <AppDataTable
        fill={false}
        resizable
        storageKey={platform ? 'sa-support-desk' : 'support-desk'}
        toolbar={(
          <FilterToolbar
            flush
            actions={(
              <>
                <IconButton label="Refresh" icon={RefreshCw} size="sm" variant="outline" onClick={reload} disabled={loading} />
                <AppButton size="sm" onClick={() => setComposerOpen(true)}>
                  <Plus size={16} aria-hidden="true" /> New ticket
                </AppButton>
              </>
            )}
          >
            <SegmentedControl
              label="Queue view"
              value={view}
              onChange={applyView}
              options={VIEWS.map((v) => ({ value: v.value, label: v.label, count: stats ? v.countOf(s) : undefined }))}
            />
            <span className="tw-text-[0.8125rem] tw-text-content-muted" aria-live="polite">
              {loading ? 'Loading…' : `${total} ticket${total === 1 ? '' : 's'}`}
            </span>
            {/* A funnel is easy to set and easy to forget — this is what says one is on. */}
            {customFilters && (
              <AppButton size="sm" variant="outline" onClick={() => setColumnFilters({})}>
                <FilterX size={16} aria-hidden="true" /> Clear filters
              </AppButton>
            )}
          </FilterToolbar>
        )}
        columns={columns}
        rows={tickets}
        rowKey={(t) => t._id}
        loading={loading}
        sort={sort}
        onSortChange={(key, direction) => setSort({ key, direction })}
        columnFilters={columnFilters}
        onColumnFilterChange={(key, value) => {
          // Cleared filters are REMOVED, not stored as undefined, so "is any
          // filter on" and the view match both read the keys honestly.
          const next = { ...columnFilters };
          if (value === undefined) delete next[key];
          else next[key] = value;
          setColumnFilters(next);
        }}
        onRowClick={(t) => navigate(`${basePath}/${t._id}`)}
        emptyState={filtersOn ? (
          <EmptyState
            icon={Inbox}
            title={view === 'needsReply' ? 'Nothing is waiting on you' : 'No tickets match'}
            description={view === 'needsReply' ? 'Every open ticket has had the last word from support.' : 'Change a column filter, or clear them to see the whole queue.'}
            action={<AppButton size="sm" variant="outline" onClick={() => setColumnFilters({})}>Show all tickets</AppButton>}
          />
        ) : (
          <EmptyState
            icon={Inbox}
            title="No tickets yet"
            description={platform ? 'Requests from institutes will land here.' : 'Requests from your students, trainers and staff will land here.'}
          />
        )}
        pagination={totalPages > 1 ? {
          page,
          totalPages,
          total,
          pageSize: PAGE_SIZE,
          onChange: setPage,
          infoLabel: 'tickets',
        } : undefined}
      />

      <NewTicketModal
        show={composerOpen}
        onHide={() => setComposerOpen(false)}
        audience="staff"
        institutes={platform ? (institutes || []) : undefined}
        ticketPath={(id) => `${basePath}/${id}`}
        onCreated={reload}
      />
    </div>
  );
};

export default SupportDeskPage;
